import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "../ui/sonner";
import { Download, Printer, FileText, AlertCircle, Link2, Check, Loader2 } from "lucide-react";
import { useMaximize, MaxButton } from "./MaxToggle";

/* 通用 PDF 预览弹窗：先看，确认无误再 下载 / 打印；actions 可追加业务按钮（如「发送给客户」），below 可放附加面板。

   2026-10-13 新增「复制回执链接」（仅当传入 sharePath 或 shareApiPath 时出现，目前只有 RECEIPT 收款回执会传）：
   需求：收款回执预览里给一个按钮，点一下复制链接，客户拿到链接可直接下载回执（无需登录）。
   实现：两种来源 ——
     · `sharePath`：后端已在列表接口里下发的公开路径（`payments[].publicReceiptPath`），零额外请求；
     · `shareApiPath`：**按需取链接**的接口（`/crm/signed/{lid}/payments/{key}/receipt-link`）。
       为什么需要它：财务手工登记收款的期次没有走过客户付款页、也就没有付款 token，
       但「INVOICE 列 › 回执」照样能预览 —— 这类期次只能等用户点复制时再按需建链接。
   最终链接 = `api.defaults.baseURL + 后端下发的 path`（前端 API 基址才是客户能访问到的公网地址）。
   ⚠️ 不能拼 PUBLIC_BASE_URL：那是前端站点地址，本地与线上都与 API 不同源（本地站点 3000 / API 8001）。
   复制用 Clipboard API，失败自动降级 execCommand（后台标签页 / 失焦 / 权限被拒时 writeText 会抛错，
   只写 try/catch 会表现为「点了没反应」）。复制成功按钮短暂变「已复制」。 */
export function PdfPreviewDialog({ api, url, title, fileName, zh, onClose, onLoaded, description, actions, below, testId = "pdf-preview", allowPrint = true, sharePath, shareApiPath }) {
  const [pdfUrl, setPdfUrl] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);
  const frame = useRef(null);
  // 项目财务合同更新提示只在内嵌 PDF 加载后确认已查看；用 ref 避免父页面传入的新回调触发重复下载。
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const loadedUrlRef = useRef("");
  const mx = useMaximize();
  const notifyLoaded = () => {
    if (!pdfUrl || loadedUrlRef.current === pdfUrl) return;
    loadedUrlRef.current = pdfUrl;
    // 等到内嵌 PDF 已触发加载事件再确认查看；关闭弹窗前未加载完成时不清红标。
    try { const pending = onLoadedRef.current?.(); pending?.catch?.(() => undefined); } catch { /* 回执失败不能阻止 PDF 阅读 */ }
  };
  useEffect(() => {
    let obj = "";
    loadedUrlRef.current = "";
    setPdfUrl(""); setErr("");
    api.get(url, { responseType: "blob" })
      .then((r) => {
        obj = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); setPdfUrl(obj);
      })
      .catch(async (e) => { let d = e?.response?.data; if (d instanceof Blob) { try { d = JSON.parse(await d.text()).detail; } catch { d = null; } } setErr(d?.detail || d || e.message); });
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [api, url]);
  const download = () => { const a = document.createElement("a"); a.href = pdfUrl; a.download = fileName || "document.pdf"; a.click(); };
  const print = () => {
    try { const w = frame.current.contentWindow; w.focus(); w.print(); } catch { window.open(pdfUrl, "_blank", "noopener"); }
  };
  // 客户可直接打开的绝对链接（API 基址 + 后端下发的公开路径）；sharePath 缺失时按需向后端取
  const apiBase = String(api.defaults.baseURL || "").replace(/\/$/, "");
  const canShare = !!(sharePath || shareApiPath);
  const [resolving, setResolving] = useState(false);
  const copyLink = async () => {
    if (!canShare) return;
    setResolving(true);
    let shareUrl = "";
    try {
      let path = sharePath;
      if (!path && shareApiPath) {
        const { data } = await api.get(shareApiPath);
        path = data?.path || "";
      }
      shareUrl = path ? `${apiBase}${path}` : "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || (zh ? "获取回执链接失败，请稍后重试" : "Failed to get the receipt link"));
      setResolving(false);
      return;
    }
    if (!shareUrl) { toast.error(zh ? "该期暂不可生成回执链接" : "Receipt link unavailable"); setResolving(false); return; }
    // 兜底复制：隐藏 textarea + execCommand，不依赖 Clipboard API 的前台 / 权限要求
    const legacyCopy = () => {
      const ta = document.createElement("textarea");
      ta.value = shareUrl; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.top = "-1000px"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select(); ta.setSelectionRange(0, shareUrl.length);
      let ok = false;
      try { ok = document.execCommand("copy"); } catch { ok = false; }
      document.body.removeChild(ta);
      return ok;
    };
    try {
      let ok = false;
      if (navigator.clipboard && window.isSecureContext) {
        try { await navigator.clipboard.writeText(shareUrl); ok = true; } catch { ok = legacyCopy(); }
      } else { ok = legacyCopy(); }
      if (!ok) throw new Error("copy failed");
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success(zh ? "回执链接已复制，可直接发给客户下载" : "Receipt link copied — send it to the customer");
    } catch { toast.error(zh ? "复制失败，请手动复制链接" : "Copy failed — please copy the link manually"); }
    setResolving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={mx.contentCls("max-w-4xl max-h-[92dvh] overflow-y-auto")} data-testid={testId} data-max={mx.max ? 1 : 0}>
        <MaxButton max={mx.max} onToggle={mx.toggle} zh={zh} testId={`${testId}-max`} />
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><FileText className="h-4 w-4 text-brand-500" />{title}</DialogTitle>
          <DialogDescription>{description || (zh ? "先核对预览内容，确认无误后再下载或打印。" : "Review the document first, then download or print.")}</DialogDescription>
        </DialogHeader>
        {err ? <p className="text-sm text-red-600 inline-flex items-center gap-1.5" data-testid={`${testId}-error`}><AlertCircle className="h-4 w-4" />{String(err)}</p> : pdfUrl
          ? <iframe ref={frame} title={title} src={`${pdfUrl}#toolbar=0&view=FitH`} onLoad={notifyLoaded} style={mx.frameStyle(below ? 420 : 210)} className="w-full h-[420px] lg:h-[560px] rounded-lg border border-slate-200 bg-slate-50" data-testid={`${testId}-frame`} />
          // 占位也套 frameStyle：放大态下与 iframe 一样用 flex 撑满剩余高度，否则固定 420px 会在底部留一大块空白
          : <div style={mx.frameStyle(below ? 420 : 210)} className="h-[420px] rounded-lg border border-dashed border-slate-200 flex items-center justify-center text-xs text-slate-400" data-testid={`${testId}-loading`}>{zh ? "正在生成 PDF…" : "Generating PDF…"}</div>}
        {below}
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} data-testid={`${testId}-close`}>{zh ? "关闭" : "Close"}</Button>
          {/* 复制回执链接：只有 RECEIPT 状态（后端下发了 publicReceiptPath 或提供了按需取链接的接口）才出现 */}
          {canShare && (
            <Button variant="outline" size="sm" disabled={resolving} onClick={copyLink} data-testid={`${testId}-copy-link`} data-copied={copied ? 1 : 0}>
              {resolving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : copied ? <Check className="h-3.5 w-3.5 mr-1 text-emerald-600" /> : <Link2 className="h-3.5 w-3.5 mr-1" />}
              {resolving ? (zh ? "生成链接…" : "Getting link…") : copied ? (zh ? "已复制" : "Copied") : (zh ? "复制回执链接" : "Copy receipt link")}
            </Button>
          )}
          {actions}
          <Button variant="outline" size="sm" disabled={!pdfUrl} onClick={download} data-testid={`${testId}-download`}><Download className="h-3.5 w-3.5 mr-1" />{zh ? "下载 PDF" : "Download PDF"}</Button>
          {allowPrint && <Button size="sm" disabled={!pdfUrl} onClick={print} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid={`${testId}-print`}><Printer className="h-3.5 w-3.5 mr-1" />{zh ? "打印" : "Print"}</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
